import { hasTenantContextChanged, TENANT_CONTEXT_CHANGED, TENANT_SWITCH_FULFILLED } from '@/features/auth/tenantSwitchActionType'
import { IMPERSONATION_EXITED } from '@/features/auth/impersonationActionTypes'

/** Actions after which the active tenant's entity data no longer applies. */
const TENANT_RESET_TYPES = new Set([TENANT_SWITCH_FULFILLED, IMPERSONATION_EXITED])

/**
 * Wraps a combined root reducer so a change of active tenant resets every
 * tenant-scoped entity slice back to its own initial state in one place,
 * instead of requiring each slice to opt in individually with its own
 * `switchTenant.fulfilled` case.
 *
 * Some slices (metrics, partitionTypes) already had their own opt-in case
 * before this existed; those still work (resetting an already-reset slice is
 * a no-op) but this is what actually covers every slice, including the ones
 * nobody had gotten to yet (categories, campuses, dashboards, reports,
 * reportTemplates, tags, widgets, forms, folders, notifications,
 * subscriptions, metricEntries, favorites, users, settings) - the gap a
 * same-session tenant switch from TenantSelectPage (which, unlike
 * UserMenu/TenantSwitcher, does not force a page reload) exposed: the old
 * tenant's entities kept rendering in the sidebar/favorites until something
 * happened to refetch them.
 *
 * Ending an impersonation (IMPERSONATION_EXITED) resets the same way: the
 * tenant the app was pinned to is no longer the session's.
 *
 * Local selections (including enterNewTenant's failed-request fallback) and
 * membership refreshes need the same reset as a completed switch request.
 * Compare the resulting tenant ID so selecting the same tenant keeps its data.
 *
 * `auth` is preserved with the action's changes - it is not tenant-scoped
 * entity data, it's the thing that just performed the switch. `hostTenant`
 * (the workspace the page's host names) is preserved too: applying it is
 * itself a switch, and resetting it would re-read the host and apply again.
 *
 * @param {Function} appReducer - the result of combineReducers(...)
 * @param {string[]} [preserveKeys] - top-level state keys to carry over as-is
 * @returns {Function} a root reducer with the same state shape as appReducer
 */
export function withTenantSwitchReset(appReducer, preserveKeys = ['auth', 'hostTenant']) {
  return function rootReducer(state, action) {
    const nextState = appReducer(state, action)
    if (state && (TENANT_RESET_TYPES.has(action.type) || hasTenantContextChanged(state, nextState))) {
      // A fresh object with only the preserved keys - combineReducers gives
      // every other key `undefined`, which is exactly what makes each of
      // those reducers fall back to its own initial state. Immutable: this
      // never touches the incoming `state` object, it only stops passing
      // most of it along.
      // A key the store doesn't have is skipped, not carried as undefined.
      const preservedState = preserveKeys.reduce((preserved, key) => {
        if (key in nextState) preserved[key] = nextState[key]
        return preserved
      }, {})
      // Initialize the scoped slices without replaying the original action:
      // it has already updated auth and could otherwise apply changes twice.
      return appReducer(preservedState, { type: TENANT_CONTEXT_CHANGED })
    }
    return nextState
  }
}
